<!-- PdfDataParser.svelte -->
<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { discountItems, parseDiscountData } from '../store.js';
    import { extractPdfContent } from '../utils/pdfProcessor.js';
    import { testBackendConnection, testAzureConnection } from '../services/backendService.js';
    import ExtractedItemsView from './ExtractedItemsView.svelte';
    import { FEATURES } from '../config.js';
    
    const dispatch = createEventDispatcher();
    
    let pdfUrl = '';
    let isLoading = false;
    let error = null;
    let parsedItems = [];
    let successMessage = '';
    let selectedSource = 'file'; // 'file' or 'url'
    let azureProcessingEnabled = FEATURES.AZURE_VISION_ENABLED;  // Flag to toggle Azure processing
    let backendEnabled = FEATURES.USE_BACKEND; // Flag to toggle backend processing
    let backendStatus = 'unknown'; // 'connected', 'disconnected', or 'unknown'
    let azureStatus = 'unknown'; // 'connected', 'disconnected', or 'unknown'
    
    // Sample data for demonstration
    const sampleDiscountItems = [
      { 
        id: 1, 
        name: 'Laxfilé hel sida', 
        category: 'seafood', 
        originalPrice: 199.00, 
        discountPrice: 149.00, 
        unit: 'kg', 
        image: 'images/salmon.jpg', 
        discount: 25,
        description: 'Hel sida, ca 1,2 kg, Fångstzon: se förp.' 
      },
      { 
        id: 2, 
        name: 'Kycklingbröstfilé', 
        category: 'meat', 
        originalPrice: 91.90, 
        discountPrice: 69.90, 
        unit: 'kg', 
        image: 'images/chicken.jpg', 
        discount: 23,
        description: 'GYLLDA, 1 kg, Ursprung: Sverige' 
      },
      { 
        id: 3, 
        name: 'Kvisttomater', 
        category: 'vegetables', 
        originalPrice: 39.90, 
        discountPrice: 24.90, 
        unit: 'kg', 
        image: 'images/tomatoes.jpg', 
        discount: 37,
        description: 'Ursprung: Spanien' 
      },
      { 
        id: 4, 
        name: 'OLW snacks', 
        category: 'snacks', 
        originalPrice: 83.70, 
        discountPrice: 59.00, 
        unit: '3-pack', 
        image: 'images/olw.jpg', 
        discount: 30,
        description: '200 g–275 g/förp. Olika sorter. 3 för 59:-' 
      },
      { 
        id: 6, 
        name: 'Gul lök', 
        category: 'vegetables', 
        originalPrice: 12.90, 
        discountPrice: 9.90, 
        unit: 'kg', 
        image: 'images/onion.jpg', 
        discount: 23,
        description: 'Ursprung: Sverige, Lösvikt' 
      },
      { 
        id: 7, 
        name: 'Röda äpplen', 
        category: 'fruit', 
        originalPrice: 45.00, 
        discountPrice: 39.90, 
        unit: '2.5kg', 
        image: 'images/apples.jpg', 
        discount: 11,
        description: 'Sort: Gala/Crimson Snow, Ursprung: Italien, 15.96 kr/kg' 
      },
    ];
    
    // Check backend and Azure connection status on component mount
    onMount(async () => {
      await checkConnections();
    });
    
    // Function to check backend and Azure connections
    async function checkConnections() {
      try {
        // Check backend connection
        const backendResult = await testBackendConnection();
        backendStatus = backendResult.message ? 'connected' : 'disconnected';
        
        if (backendStatus === 'connected' && backendEnabled) {
          // Check Azure connection
          const azureResult = await testAzureConnection();
          azureStatus = azureResult.success ? 'connected' : 'disconnected';
        }
      } catch (error) {
        console.error('Error checking connections:', error);
        backendStatus = 'disconnected';
        azureStatus = 'unknown';
      }
    }
    
    // Function to handle loading sample data
    function loadSampleData() {
      isLoading = true;
      error = null;
      successMessage = '';
      
      // Ensure all numeric values are properly formatted
      const processedSampleItems = sampleDiscountItems.map(item => ({
        ...item,
        discountPrice: parseFloat(item.discountPrice) || 0,
        originalPrice: parseFloat(item.originalPrice) || 0,
        discount: parseFloat(item.discount) || 0
      }));
      
      // Simulate loading delay
      setTimeout(() => {
        parseDiscountData(processedSampleItems);
        parsedItems = processedSampleItems;
        successMessage = `Laddat ${parsedItems.length} exempel-erbjudanden!`;
        isLoading = false;
      }, 1000);
    }
    
    // Function to parse the PDF when a file is selected
    async function handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      isLoading = true;
      error = null;
      successMessage = '';
      parsedItems = [];
      
      try {
        // Read the file as ArrayBuffer
        const fileBuffer = await readFileAsArrayBuffer(file);
        
        // Process the PDF with the selected method
        parsedItems = await extractPdfContent(fileBuffer, file);
        
        if (parsedItems.length > 0) {
          successMessage = `Hittade ${parsedItems.length} erbjudanden i PDF-filen!`;
        } else {
          error = 'Inga livsmedel hittades i PDF-filen. Försök med en annan fil.';
        }
        
        // Notify parent component that parsing is complete
        dispatch('parsedData', parsedItems);
      } catch (e) {
        console.error('Error parsing PDF:', e);
        error = 'Det gick inte att läsa PDF-filen. Kontrollera att filen är en giltig reklamblad i PDF-format.';
      } finally {
        isLoading = false;
      }
    }
    
    // Function to handle parsing a PDF from a URL
    async function handleUrlParse() {
      if (!pdfUrl) {
        error = 'Vänligen ange en giltig URL till veckans erbjudanden';
        return;
      }
      
      isLoading = true;
      error = null;
      successMessage = '';
      parsedItems = [];
      
      try {
        // Fetch the PDF from the URL
        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Get the PDF as ArrayBuffer
        const fileBuffer = await response.arrayBuffer();
        
        // Create a File object from the buffer
        const file = new File([fileBuffer], 'discount-leaflet.pdf', { type: 'application/pdf' });
        
        // Process the PDF with Azure AI Vision
        parsedItems = await extractPdfContent(fileBuffer, file);
        
        if (parsedItems.length > 0) {
          successMessage = `Hittade ${parsedItems.length} erbjudanden från URL!`;
        } else {
          error = 'Inga livsmedel hittades i PDF-filen. Försök med en annan URL.';
        }
        
        // Notify parent component that parsing is complete
        dispatch('parsedData', parsedItems);
      } catch (e) {
        console.error('Error fetching PDF:', e);
        error = 'Det gick inte att hämta filen från URL. Kontrollera att URL:en är korrekt och att filen är tillgänglig.';
      } finally {
        isLoading = false;
      }
    }
    
    // Helper function to read a file as ArrayBuffer
    function readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    }
    
    // Function to handle adding all extracted items to the store
    function handleItemsAdded(event) {
      const addedItems = event.detail;
      // Get the current items from the store
      const currentItems = $discountItems || [];
      
      // Create copies with proper numeric conversions for the prices
      const processedItems = addedItems.map(item => ({
        ...item,
        discountPrice: parseFloat(item.discountPrice) || 0,
        originalPrice: parseFloat(item.originalPrice) || 0,
        discount: parseFloat(item.discount) || 0
      }));
      
      // Add the processed items to the store
      parseDiscountData([...currentItems, ...processedItems]);
      successMessage = `${addedItems.length} varor lades till i listan.`;
    }
    
    // Function to handle adding a single item to the store
    function handleItemAdded(event) {
      const addedItem = event.detail;
      // Get the current items from the store
      const currentItems = $discountItems || [];
      
      // Create a copy with proper numeric conversions
      const processedItem = {
        ...addedItem,
        discountPrice: parseFloat(addedItem.discountPrice) || 0,
        originalPrice: parseFloat(addedItem.originalPrice) || 0,
        discount: parseFloat(addedItem.discount) || 0
      };
      
      // Add the processed item to the store
      parseDiscountData([...currentItems, processedItem]);
      successMessage = `${addedItem.name} lades till i listan.`;
    }
    
    // Function to toggle Azure processing
    function toggleAzureProcessing() {
      azureProcessingEnabled = !azureProcessingEnabled;
      FEATURES.AZURE_VISION_ENABLED = azureProcessingEnabled;
    }
    
    // Function to toggle backend processing
    function toggleBackendProcessing() {
      backendEnabled = !backendEnabled;
      FEATURES.USE_BACKEND = backendEnabled;
      
      // If we enable backend, check connections
      if (backendEnabled) {
        checkConnections();
      }
    }
</script>
  
<div class="pdf-parser">
  <h2>Importera veckans erbjudanden</h2>
  
  <div class="import-source-selector">
    <button 
      class:active={selectedSource === 'file'} 
      on:click={() => selectedSource = 'file'}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
      </svg>
      Från PDF-fil
    </button>
    <button 
      class:active={selectedSource === 'url'} 
      on:click={() => selectedSource = 'url'}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
      </svg>
      Från URL
    </button>
    <button 
      class:active={selectedSource === 'sample'} 
      on:click={() => selectedSource = 'sample'}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="9" y1="9" x2="15" y2="9"></line>
        <line x1="9" y1="15" x2="15" y2="15"></line>
        <line x1="9" y1="12" x2="15" y2="12"></line>
      </svg>
      Exempeldata
    </button>
  </div>
  
  <div class="connection-status">
    <div class="status-item">
      <span class="status-label">Backend:</span>
      <span class="status-indicator" class:connected={backendStatus === 'connected'} class:disconnected={backendStatus === 'disconnected'}>
        {backendStatus === 'connected' ? 'Ansluten' : backendStatus === 'disconnected' ? 'Ej ansluten' : 'Okänd'}
      </span>
    </div>
    <div class="status-item">
      <span class="status-label">Azure AI:</span>
      <span class="status-indicator" class:connected={azureStatus === 'connected'} class:disconnected={azureStatus === 'disconnected'}>
        {azureStatus === 'connected' ? 'Ansluten' : azureStatus === 'disconnected' ? 'Ej ansluten' : 'Okänd'}
      </span>
    </div>
  </div>
  
  <div class="import-methods">
    {#if selectedSource === 'file'}
      <div class="method active">
        <div class="method-header">
          <p>Ladda upp butikens veckoblad i PDF-format</p>
          <div class="toggle-options">
            <div class="toggle-option">
              <label>
                <input type="checkbox" bind:checked={backendEnabled} on:change={toggleBackendProcessing}>
                <span>Använd backend</span>
              </label>
              <span class="toggle-help" title="Använd backend-servern för att bearbeta PDF:en med Azure AI.">?</span>
            </div>
            <div class="toggle-option">
              <label>
                <input type="checkbox" bind:checked={azureProcessingEnabled} on:change={toggleAzureProcessing}>
                <span>Azure AI Vision</span>
              </label>
              <span class="toggle-help" title="Azure AI Vision används för att analysera bilderna i PDF:en och extrahera produktinformation med högre precision.">?</span>
            </div>
          </div>
        </div>
        <label class="file-input-label">
          <input 
            type="file" 
            accept=".pdf" 
            on:change={handleFileSelect}
            disabled={isLoading}
          />
          <span>Välj fil</span>
        </label>
      </div>
    {:else if selectedSource === 'url'}
      <div class="method active">
        <div class="method-header">
          <p>Ange URL till veckans erbjudanden online</p>
          <div class="toggle-options">
            <div class="toggle-option">
              <label>
                <input type="checkbox" bind:checked={backendEnabled} on:change={toggleBackendProcessing}>
                <span>Använd backend</span>
              </label>
              <span class="toggle-help" title="Använd backend-servern för att bearbeta PDF:en med Azure AI.">?</span>
            </div>
            <div class="toggle-option">
              <label>
                <input type="checkbox" bind:checked={azureProcessingEnabled} on:change={toggleAzureProcessing}>
                <span>Azure AI Vision</span>
              </label>
              <span class="toggle-help" title="Azure AI Vision används för att analysera bilderna i PDF:en och extrahera produktinformation med högre precision.">?</span>
            </div>
          </div>
        </div>
        <div class="url-input">
          <input 
            type="text" 
            bind:value={pdfUrl} 
            placeholder="https://www.example.se/veckans-erbjudanden.pdf"
            disabled={isLoading}
          />
          <button on:click={handleUrlParse} disabled={isLoading}>Hämta</button>
        </div>
      </div>
    {:else if selectedSource === 'sample'}
      <div class="method active">
        <p>Ladda exempeldata med några vanliga erbjudanden</p>
        <button class="sample-data-button" on:click={loadSampleData} disabled={isLoading}>
          Ladda exempeldata
        </button>
      </div>
    {/if}
  </div>
  
  {#if isLoading}
    <div class="loading">
      <svg class="spinner" viewBox="0 0 50 50">
        <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
      </svg>
      <p>Analyserar data{backendEnabled ? ' via backend' : ''}{azureProcessingEnabled ? ' med Azure AI Vision' : ''}...</p>
    </div>
  {/if}
  
  {#if error}
    <div class="error">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <p>{error}</p>
    </div>
  {/if}
  
  {#if successMessage}
    <div class="success">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <p>{successMessage}</p>
    </div>
  {/if}

  <!-- Display extracted items -->
  {#if parsedItems.length > 0 && !isLoading}
    <ExtractedItemsView 
      items={parsedItems} 
      isProcessing={isLoading}
      on:itemAdded={handleItemAdded}
      on:itemsAdded={handleItemsAdded}
    />
  {/if}

  {#if parsedItems.length > 0 && !isLoading}
    <div class="parser-footer">
      <p class="parser-note">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        I den färdiga appen skulle data importeras automatiskt från butiken.
      </p>
    </div>
  {/if}
</div>
  
<style>
  .pdf-parser {
    background-color: #f8f8f8;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 30px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
  
  h2 {
    color: #333;
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 18px;
    border-bottom: 1px solid #ddd;
    padding-bottom: 10px;
  }
  
  .import-source-selector {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
  }
  
  .import-source-selector button {
    flex: 1;
    padding: 10px;
    background-color: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
  }
  
  .import-source-selector button:hover {
    background-color: #e0e0e0;
  }
  
  .import-source-selector button.active {
    background-color: #e91e63;
    color: white;
    border-color: #e91e63;
  }
  
  .connection-status {
    display: flex;
    gap: 20px;
    margin-bottom: 15px;
    padding: 10px 15px;
    background-color: #f0f0f0;
    border-radius: 5px;
  }
  
  .status-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .status-label {
    font-weight: 500;
    font-size: 14px;
  }
  
  .status-indicator {
    font-size: 14px;
    padding: 3px 8px;
    border-radius: 3px;
    background-color: #eee;
  }
  
  .status-indicator.connected {
    background-color: #4caf50;
    color: white;
  }
  
  .status-indicator.disconnected {
    background-color: #f44336;
    color: white;
  }
  
  .import-methods {
    margin-bottom: 20px;
  }
  
  .method {
    padding: 20px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .method p {
    color: #666;
    font-size: 14px;
    margin-top: 0;
    margin-bottom: 15px;
  }
  
  .method-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }
  
  .toggle-options {
    display: flex;
    gap: 15px;
  }
  
  .toggle-option {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .toggle-option label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #666;
    cursor: pointer;
  }
  
  .toggle-help {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #ddd;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    cursor: help;
  }
  
  .file-input-label {
    display: inline-block;
    cursor: pointer;
  }
  
  .file-input-label input {
    display: none;
  }
  
  .file-input-label span {
    display: inline-block;
    background-color: #e91e63;
    color: white;
    padding: 10px 15px;
    border-radius: 5px;
    font-weight: 500;
    transition: background-color 0.2s;
  }
  
  .file-input-label span:hover {
    background-color: #d81b60;
  }
  
  .file-input-label:disabled span {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  .url-input {
    display: flex;
    width: 100%;
  }
  
  .url-input input {
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 5px 0 0 5px;
    outline: none;
    font-size: 14px;
  }
  
  .url-input input:focus {
    border-color: #e91e63;
  }
  
  .url-input button {
    padding: 10px 15px;
    background-color: #e91e63;
    color: white;
    border: none;
    border-radius: 0 5px 5px 0;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .url-input button:hover {
    background-color: #d81b60;
  }
  
  .url-input button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  .sample-data-button {
    width: 100%;
    padding: 10px 15px;
    background-color: #e91e63;
    color: white;
    border: none;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .sample-data-button:hover {
    background-color: #d81b60;
  }
  
  .sample-data-button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  .loading {
    display: flex;
    align-items: center;
    padding: 15px;
    background-color: #e3f2fd;
    border-radius: 5px;
    color: #1976d2;
    margin-top: 15px;
  }
  
  .spinner {
    width: 24px;
    height: 24px;
    margin-right: 10px;
    animation: rotate 2s linear infinite;
  }
  
  .path {
    stroke: #1976d2;
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
  }
  
  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }
  
  @keyframes dash {
    0% {
      stroke-dasharray: 1, 150;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -35;
    }
    100% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -124;
    }
  }
  
  .error {
    display: flex;
    align-items: center;
    padding: 15px;
    background-color: #ffebee;
    border-radius: 5px;
    color: #d32f2f;
    margin-top: 15px;
  }
  
  .error svg {
    margin-right: 10px;
  }
  
  .success {
    display: flex;
    align-items: center;
    padding: 15px;
    background-color: #e8f5e9;
    border-radius: 5px;
    color: #388e3c;
    margin-top: 15px;
  }
  
  .success svg {
    margin-right: 10px;
  }
  
  .parser-footer {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eee;
  }
  
  .parser-note {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #666;
    margin: 0;
  }
  
  .parser-note svg {
    margin-right: 8px;
    color: #999;
  }
  
  @media (max-width: 768px) {
    .import-source-selector {
      flex-direction: column;
    }
    
    .method-header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .toggle-options {
      margin-top: 10px;
      flex-direction: column;
    }
    
    .connection-status {
      flex-direction: column;
      gap: 5px;
    }
  }
</style>
